import React, { ReactNode, createContext, useCallback, useContext, useState } from 'react';
import {
  ReferralConfigAttributes,
  ReferralConfigInput,
} from '@loyalty/shared-common/gamification-api-client';
import { useAlerts } from '@qualifio/ui-commons';
import { useTranslation } from 'react-i18next';
import { Loader } from '../shared/layout/Loader';
import { useApi } from '../shared/api-client/ApiClientProvider';
import { useLoaders } from '../shared/context/context-utils';
import { useProgram } from '../loyalty/ProgramContext';
import { useSelectiveEffect } from '../shared/use-selective-effect';

export interface ReferralConfigContextInterface {
  referralConfig: ReferralConfigAttributes | undefined;
  getReferralConfig: () => Promise<void>;
  upsertReferralConfig: (referralConfig: ReferralConfigInput) => Promise<void>;
}

export const ReferralConfigContext = createContext<ReferralConfigContextInterface>({} as any);

export function useReferralConfig() {
  return useContext(ReferralConfigContext);
}

export function ReferralConfigProvider({ children }: { children: ReactNode }) {
  const { t } = useTranslation();
  const { notify } = useAlerts();
  const { program } = useProgram();
  const { gamificationApi } = useApi();
  const [referralConfig, setReferralConfig] = useState<ReferralConfigAttributes | undefined>(
    undefined,
  );

  const { addLoader, removeLoader, isLoading } = useLoaders({ getReferralConfig: true });

  const getReferralConfig = useCallback(() => {
    addLoader('getReferralConfig');

    return gamificationApi
      .getReferralConfig(program.id)
      .then(referralConfig => {
        if (referralConfig) {
          setReferralConfig(referralConfig);
        }
      })
      .catch(error => notify(t('referral.errors.failed-get-referral-config', { error }), 'error'))
      .finally(() => removeLoader('getReferralConfig'));
  }, [addLoader, gamificationApi, notify, program.id, removeLoader, t]);

  const upsertReferralConfig = useCallback(
    (referralConfig: ReferralConfigInput) => {
      addLoader('upsertReferralConfig');

      return gamificationApi
        .upsertReferralConfig(program.id, referralConfig)
        .then(referralConfig => {
          setReferralConfig(referralConfig);
          notify(t('referral.success.config-saved'), 'success');
        })
        .catch(error =>
          notify(t('referral.errors.failed-save-referral-config', { error }), 'error'),
        )
        .finally(() => removeLoader('upsertReferralConfig'));
    },
    [addLoader, gamificationApi, notify, program.id, removeLoader, t],
  );

  useSelectiveEffect(
    () => {
      getReferralConfig();
    },
    [getReferralConfig, program.id],
    [program.id],
  );

  const context = {
    referralConfig,
    getReferralConfig,
    upsertReferralConfig,
  };

  return isLoading('getReferralConfig') ? (
    <Loader />
  ) : (
    <ReferralConfigContext.Provider value={context}>{children}</ReferralConfigContext.Provider>
  );
}
